package ru.chm.gradereport.ui.components

import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.platform.openUrl
import ru.chm.gradereport.ui.theme.GradeTheme

/** http(s) links inside text: `https://...`, `http://...` or bare `www.`. */
private val URL_REGEX = Regex(
    pattern = """(?:https?://|www\.)[^\s<>()]+[^\s<>().,;:!?"'\]]""",
    options = setOf(RegexOption.IGNORE_CASE),
)

private const val TAG_URL = "url"

/**
 * Text with clickable http(s) links (opened in a new browser tab via [openUrl]).
 * The rest of the text stays selectable like [SelectableText]; hovering a link
 * shows the pointer (hand) cursor, like on regular websites.
 */
@Composable
fun LinkedText(
    text: String,
    modifier: Modifier = Modifier,
    color: Color = GradeTheme.colors.textPrimary,
    linkColor: Color = GradeTheme.colors.blueAccent,
    fontSize: TextUnit = 14.sp,
    lineHeight: TextUnit = TextUnit.Unspecified,
) {
    if (!text.contains("://") && !text.contains("www.")) {
        // Fast path: no links, plain selectable text.
        SelectableText(
            text = text,
            modifier = modifier,
            color = color,
            fontSize = fontSize,
            lineHeight = lineHeight,
        )
        return
    }

    val annotated = remember(text) { buildLinkedString(text, linkColor) }
    val layoutResult = remember { mutableStateOf<TextLayoutResult?>(null) }
    var hoverLink by remember { mutableStateOf(false) }

    SelectionContainer(modifier = modifier) {
        Text(
            text = annotated,
            color = color,
            fontSize = fontSize,
            lineHeight = lineHeight,
            onTextLayout = { layoutResult.value = it },
            modifier = Modifier
                // overrideDescendants: the selectable text applies its own Text
                // (I-beam) icon deeper in the modifier chain — ours must win.
                .pointerHoverIcon(
                    if (hoverLink) PointerIcon.Hand else PointerIcon.Default,
                    overrideDescendants = true,
                )
                .pointerInput(annotated) {
                    awaitPointerEventScope {
                        var pressPosition: Offset? = null
                        while (true) {
                            val event = awaitPointerEvent()
                            val change = event.changes.firstOrNull() ?: continue
                            val layout = layoutResult.value
                            when (event.type) {
                                PointerEventType.Move -> {
                                    val offset = layout
                                        ?.getOffsetForPosition(change.position)
                                        ?: -1
                                    hoverLink = offset >= 0 &&
                                        annotated
                                            .getStringAnnotations(TAG_URL, offset, offset)
                                            .isNotEmpty()
                                }
                                PointerEventType.Press -> pressPosition = change.position
                                PointerEventType.Release -> {
                                    val start = pressPosition
                                    pressPosition = null
                                    val isTap = start != null &&
                                        (change.position - start).getDistance() < 24f
                                    val offset = if (isTap) {
                                        layout?.getOffsetForPosition(change.position) ?: -1
                                    } else {
                                        -1
                                    }
                                    val link = if (offset >= 0) {
                                        annotated
                                            .getStringAnnotations(TAG_URL, offset, offset)
                                            .firstOrNull()
                                    } else {
                                        null
                                    }
                                    if (link != null) {
                                        // Consume only when the tap actually hits a link —
                                        // otherwise the click passes through to elements
                                        // behind/around the text block.
                                        change.consume()
                                        openUrl(link.item)
                                    }
                                }
                            }
                        }
                    }
                },
        )
    }
}

private fun buildLinkedString(text: String, linkColor: Color): AnnotatedString = buildAnnotatedString {
    var lastEnd = 0
    for (match in URL_REGEX.findAll(text)) {
        val start = match.range.first
        val end = match.range.last + 1
        if (start > lastEnd) append(text.substring(lastEnd, start))
        val url = match.value
        pushStringAnnotation(TAG_URL, normalizeUrl(url))
        withStyle(SpanStyle(color = linkColor, textDecoration = TextDecoration.Underline)) {
            append(url)
        }
        pop()
        lastEnd = end
    }
    if (lastEnd < text.length) append(text.substring(lastEnd))
}

/** Bare `www.` links need a scheme for [openUrl]. */
private fun normalizeUrl(url: String): String =
    if (url.startsWith("www.", ignoreCase = true)) "https://$url" else url
